import { mafldCalculatorKeys as k } from "@/i18n/mafld-calculator-keys"

/**
 * MAFLD: metabolic dysfunction associated fatty liver disease, as the 2020
 * international expert consensus defines it.
 *
 * Unlike every other calculator on this site, MAFLD is not a score. It is a
 * set of positive criteria, and the answer is a verdict rather than a number.
 * The definition has exactly two halves and both must hold:
 *
 *   1. evidence of hepatic steatosis (fat in the liver), and
 *   2. at least one of three metabolic routes:
 *        a. overweight or obesity, meaning a BMI of 25 or more
 *        b. type 2 diabetes
 *        c. a normal BMI PLUS two or more of seven metabolic abnormalities
 *
 * Source: Eslam M, Newsome PN, Sarin SK et al., "A new definition for
 * metabolic dysfunction-associated fatty liver disease: an international
 * expert consensus statement", Journal of Hepatology 2020;73:202-209.
 *
 * What makes this implementable on a web page is that the consensus accepts
 * three kinds of steatosis evidence: imaging, histology, OR "blood
 * biomarkers/scores". So a reader who has had a scan can declare the answer,
 * and a reader who has not gets the steatosis half estimated from a validated
 * biomarker score. The one used here is the Fatty Liver Index (see
 * `fattyLiverIndex`), which is also what the large MAFLD prevalence studies
 * use when they have no imaging.
 *
 * The consensus's route (c) lists SEVEN abnormalities; this module reads SIX
 * of them. The omitted one is HOMA-IR, which needs a fasting insulin, and
 * fasting insulin is not on a Swiss check-up panel. Leaving it out can only
 * ever move a lean reader from "meets the criteria" to "does not", never the
 * other way, so the direction of the error is stated on the page rather than
 * hidden. See `CRITERION_DEFS`.
 *
 * Why `lib/` and not beside the route, for the reason lib/smaf5.ts gives: the
 * widget, the page's two reference tables AND the markdown mirror in
 * `lib/native-markdown/mafld-calculator.ts` all read this one source, so a
 * changed threshold cannot drift between them, and the whole thing is
 * testable without rendering React.
 *
 * Nothing here is per-country. The consensus publishes ethnicity-specific
 * BMI and waist cut-offs (lower for Asian populations) and this module
 * implements the Caucasian set only, which is the one that applies to the
 * market the page is gated to. That is a real limitation rather than an
 * oversight, and `mafld_limit4Body` says so in as many words.
 */

export type MafldSex = MafldSexOption["value"]

/**
 * `as const` is load-bearing here and in every other `*_DEFS` below, for the
 * reason the BMR calculator's `SEX_OPTIONS` documents: annotating `labelKey`
 * as `string` widens it out of the catalog's key union and next-intl's typed
 * `t()` rejects it outright.
 */
const SEX_DEFS = [
  { value: "female", labelKey: k.calcSexFemale },
  { value: "male", labelKey: k.calcSexMale },
] as const

export type MafldSexOption = (typeof SEX_DEFS)[number]

export const SEX_OPTIONS: readonly MafldSexOption[] = SEX_DEFS

/**
 * How the steatosis half of the definition is answered.
 *
 * Three options rather than two, because "nobody has looked" is the honest
 * answer for most readers and it is NOT the same as "a scan found nothing".
 * A scan that ruled fat out settles the question against MAFLD; never having
 * been scanned is what the Fatty Liver Index is for.
 *
 * `excluded` exists so that a reader who HAS been scanned is not handed an
 * estimate that contradicts their own imaging. Imaging beats a biomarker
 * score, in both directions, and the widget uses this answer in preference to
 * the index whenever it is given.
 */
const STEATOSIS_DEFS = [
  { value: "confirmed", labelKey: k.calcSteatosisConfirmed },
  { value: "excluded", labelKey: k.calcSteatosisExcluded },
  { value: "unknown", labelKey: k.calcSteatosisUnknown },
] as const

export type MafldSteatosisOption = (typeof STEATOSIS_DEFS)[number]

export type SteatosisAnswer = MafldSteatosisOption["value"]

export const STEATOSIS_OPTIONS: readonly MafldSteatosisOption[] =
  STEATOSIS_DEFS

// --- Plausibility bounds ----------------------------------------------------

/**
 * Outside these a value is almost certainly a typo, the wrong unit, or an
 * acute illness none of this is calibrated for.
 *
 * Height and weight match the ones the BMR and sMAF-5 calculators accept
 * (lib/bmr.ts, lib/smaf5.ts). Two calculators on the same site disagreeing
 * about what counts as a plausible height would be a defect in its own right,
 * and these are anthropometric bounds rather than anything this definition has
 * an opinion about.
 *
 * Every bound here, triglycerides included, is in the unit this module
 * computes in: mmol/L for the lipid, U/L for GGT, the units Swiss
 * laboratories report and the papers use. The form may ask its reader in
 * mg/dL instead — German labs report lipids that way, so in that market the
 * triglyceride field defaults to mg/dL and offers the switch back
 * (feature:tools-mgdl-lipids, lib/lipid-units.ts) — but the conversion
 * happens in the widget, before anything here sees a number, so a market's
 * unit convention never reaches this file.
 *
 * That division is the point. This module keeps one scale and one set of
 * bounds, which is what makes `MAX_TRIGLYCERIDES` a usable guard against the
 * mistake it was written for: a mg/dL figure entered while mmol/L is the
 * selected unit. The mirror-image mistake, a mmol/L figure entered while
 * mg/dL is selected, converts to a value inside every range here and so
 * cannot be caught at this layer; `looksLikeMmolPerLEntry` catches it at the
 * one that knows which unit was asked for. The other conversion, the index's
 * own, is internal and invisible (see `TRIGLYCERIDE_MG_DL_PER_MMOL_L`).
 */
export const MIN_HEIGHT_CM = 90
export const MAX_HEIGHT_CM = 250
export const MIN_WEIGHT_KG = 20
export const MAX_WEIGHT_KG = 400
export const MIN_WAIST_CM = 40
export const MAX_WAIST_CM = 200
/** mmol/L. 0.2 is below any reported value; 30 is past severe familial disease. */
export const MIN_TRIGLYCERIDES = 0.2
export const MAX_TRIGLYCERIDES = 30
/** U/L. GGT runs to the low thousands in obstruction, hence the wide ceiling. */
export const MIN_GGT = 3
export const MAX_GGT = 3000

/**
 * Bounds on the BMI the height and weight produce.
 *
 * Checked separately because the two field bounds do not imply it: 40 kg at
 * 190 cm and 300 kg at 150 cm are each a plausible-looking number in a
 * plausible-looking box, and only their ratio gives them away.
 */
export const MIN_BMI = 12
export const MAX_BMI = 70

// --- The Fatty Liver Index --------------------------------------------------

/**
 * The unit bridge, and the single most dangerous number in this file.
 *
 * The Fatty Liver Index was published with triglycerides in mg/dL, which is
 * the American convention. Swiss laboratories report mmol/L, so the form asks
 * for mmol/L and this constant converts. 1 mmol/L of triglyceride is 88.57
 * mg/dL, from a mean molar mass of about 885.4 g/mol for the triglyceride
 * mixture the assay is standardised against.
 *
 * It is a constant with a name rather than an inline `* 88.57` for one
 * reason: the coefficient it feeds sits inside a logarithm, so getting the
 * factor wrong shifts every result by a fixed amount and nothing about the
 * output looks broken. `__tests__/mafld-calculator.test.ts` pins the index
 * against a longhand reference that does the conversion independently.
 */
export const TRIGLYCERIDE_MG_DL_PER_MMOL_L = 88.57

/** The index runs 0 to 100. Not a percentage: it is a rescaled logistic. */
export const FLI_MIN = 0
export const FLI_MAX = 100

/**
 * The two published cut-offs: below 30 rules steatosis out, 60 and above
 * rules it in, and the span between them decides nothing.
 *
 * Both bounds are inclusive on the side that decides: 30 is already
 * indeterminate and 60 is already positive, which is how the paper quotes
 * them ("< 30" and ">= 60"). Pinned in the unit test, because "both cut-offs
 * exclusive" is the shape a reader of lib/fib4.ts would assume.
 */
export const FLI_RULES_OUT_BELOW = 30
export const FLI_RULES_IN_FROM = 60

export interface FliInput {
  /** kg/m^2, from `bmiFrom`. */
  bmi: number
  /** Waist circumference, cm. */
  waistCm: number
  /** Triglycerides, mmol/L, as a Swiss report writes them. */
  triglycerides: number
  /** Gamma-glutamyl transferase, U/L. */
  ggt: number
}

/** BMI from the two fields the form asks for. */
export function bmiFrom(heightCm: number, weightKg: number): number {
  const heightM = heightCm / 100
  return weightKg / (heightM * heightM)
}

/** The BMI as it is shown beside the result: one decimal, as everywhere else. */
export function roundBmi(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * The Fatty Liver Index, 0 to 100.
 *
 *   L   = 0.953 x ln(triglycerides in mg/dL)
 *       + 0.139 x BMI
 *       + 0.718 x ln(GGT)
 *       + 0.053 x waist circumference in cm
 *       - 15.745
 *   FLI = e^L / (1 + e^L) x 100
 *
 * Source: Bedogni G, Bellentani S, Miglioli L et al., "The Fatty Liver Index:
 * a simple and accurate predictor of hepatic steatosis in the general
 * population", BMC Gastroenterology 2006;6:33.
 *
 * It is here to supply the steatosis half of the MAFLD definition for a reader
 * who has never had a scan, which the consensus explicitly permits ("blood
 * biomarkers/scores"). It is a rescaled logistic, so the value is bounded to
 * 0 and 100 by construction and never needs clamping: what it cannot do is
 * behave like a probability, which is why the page never prints it with a
 * percent sign.
 *
 * The two terms worth understanding, because they are the ones a reader will
 * question:
 *
 *   - triglycerides and GGT both enter through a natural log, so it is the
 *     ORDER of magnitude that counts. Doubling a GGT of 20 moves the index
 *     about as much as doubling one of 40.
 *   - waist enters raw and linearly, and at 0.053 per cm it is the term that
 *     moves the index most over a realistic range: 20 cm of waist is worth
 *     more than tripling the GGT.
 */
export function fattyLiverIndex({
  bmi,
  waistCm,
  triglycerides,
  ggt,
}: FliInput): number {
  const triglyceridesMgDl = triglycerides * TRIGLYCERIDE_MG_DL_PER_MMOL_L
  const l =
    0.953 * Math.log(triglyceridesMgDl) +
    0.139 * bmi +
    0.718 * Math.log(ggt) +
    0.053 * waistCm -
    15.745
  return (Math.exp(l) / (1 + Math.exp(l))) * 100
}

/**
 * The index as it is shown to the reader: one decimal.
 *
 * Callers must band THIS value, not the raw one, so the label always describes
 * the number on screen. A raw 59.96 displays as "60.0", which the page's own
 * table calls steatosis likely, so banding the raw value would print 60.0
 * under an indeterminate label. Same rule as `roundScore` in lib/smaf5.ts, and
 * the same regression.
 */
export function roundFli(value: number): number {
  return Math.round(value * 10) / 10
}

export type FliBand = FliBandDef["id"]

/**
 * The three Fatty Liver Index bands, low to high, with what the paper reports
 * for each cut-off. `fliBandFor` walks this in order and the page's index
 * table renders one row per entry.
 *
 * The accuracy column is what makes the table worth printing: the index is
 * asymmetric, and a reader is entitled to know that it is much better at
 * ruling steatosis out than at ruling it in. Below 30 it misses about 13 in
 * 100 fatty livers; at 60 and above it wrongly flags about 14 in 100 clean
 * ones.
 */
const FLI_BAND_DEFS = [
  {
    id: "unlikely",
    labelKey: k.fliBandUnlikely,
    meaningKey: k.fliMeaningUnlikely,
    thresholdKey: k.fliThrUnlikely,
    accuracyKey: k.fliAccUnlikely,
  },
  {
    id: "indeterminate",
    labelKey: k.fliBandIndeterminate,
    meaningKey: k.fliMeaningIndeterminate,
    thresholdKey: k.fliThrIndeterminate,
    accuracyKey: k.fliAccIndeterminate,
  },
  {
    id: "likely",
    labelKey: k.fliBandLikely,
    meaningKey: k.fliMeaningLikely,
    thresholdKey: k.fliThrLikely,
    accuracyKey: k.fliAccLikely,
  },
] as const

export type FliBandDef = (typeof FLI_BAND_DEFS)[number]

export const FLI_BAND_ORDER: readonly FliBandDef[] = FLI_BAND_DEFS

export const FLI_BANDS: Record<FliBand, FliBandDef> = Object.fromEntries(
  FLI_BAND_DEFS.map((b) => [b.id, b])
) as Record<FliBand, FliBandDef>

/**
 * Which band an index falls into. Pass a value from `roundFli` (see the note
 * there).
 */
export function fliBandFor(fli: number): FliBand {
  if (fli >= FLI_RULES_IN_FROM) return "likely"
  if (fli < FLI_RULES_OUT_BELOW) return "unlikely"
  return "indeterminate"
}

// --- The metabolic half -----------------------------------------------------

/** A BMI of 25 or more satisfies the definition's first route on its own. */
export const OVERWEIGHT_BMI_FROM = 25

/**
 * Waist circumference thresholds, cm, from the consensus's route (c).
 *
 * Sex-specific, which is why the form asks for sex at all beyond the FLI: 102
 * for men and 88 for women, the Caucasian set. These are the IDF/ATP III
 * numbers the consensus adopts, NOT the lower Asian ones (90 and 80), which
 * this module does not implement. See the module comment.
 */
export const WAIST_FROM: Record<MafldSex, number> = {
  female: 88,
  male: 102,
}

/** mmol/L. The consensus quotes 1.70, which is 150 mg/dL. */
export const TRIGLYCERIDES_FROM = 1.7

/** Route (c) needs two of the criteria below, not one. */
export const LEAN_CRITERIA_NEEDED = 2

/**
 * The metabolic abnormalities of the consensus's route (c), in its own order.
 *
 * `source` is the half that matters to every caller:
 *
 *   - `derived` criteria are computed from values the form already collects
 *     for the Fatty Liver Index, so the reader never answers them twice and
 *     cannot answer them inconsistently. Waist and triglycerides are both in
 *     the index AND in this list, which is the coincidence that makes this
 *     form as short as it is.
 *   - `declared` criteria are ticked by the reader, because the values behind
 *     them (a blood pressure, an HDL, an HbA1c, a hs-CRP) are four more boxes
 *     for four yes/no answers, and each threshold is printed on its own label
 *     so the tick is an informed one.
 *
 * An unticked declared box counts as "does not apply". That is the one place
 * this page departs from the sMAF-5 rule that nothing which moves the answer
 * may carry a default, and it departs knowingly: a checkbox has no unset
 * state a reader would recognise, and the error it can cause runs one way
 * only. Missing a criterion can only lower the count, and the count only ever
 * decides route (c), so the worst case is telling a lean reader they do not
 * meet a definition they do. `assessMafld` returns `leanUnanswered` for
 * exactly that case and the widget shows a note when it is set.
 *
 * HOMA-IR is the seventh criterion in the consensus and is absent here: it
 * needs a fasting insulin, which a Swiss check-up panel does not carry. Same
 * one-way error, same note.
 */
const CRITERION_DEFS = [
  {
    id: "waist",
    source: "derived",
    labelKey: k.critWaist,
    thresholdKey: k.critWaistThr,
  },
  {
    id: "bloodPressure",
    source: "declared",
    labelKey: k.critBloodPressure,
    thresholdKey: k.critBloodPressureThr,
  },
  {
    id: "triglycerides",
    source: "derived",
    labelKey: k.critTriglycerides,
    thresholdKey: k.critTriglyceridesThr,
  },
  {
    id: "hdl",
    source: "declared",
    labelKey: k.critHdl,
    thresholdKey: k.critHdlThr,
  },
  {
    id: "prediabetes",
    source: "declared",
    labelKey: k.critPrediabetes,
    thresholdKey: k.critPrediabetesThr,
  },
  {
    id: "crp",
    source: "declared",
    labelKey: k.critCrp,
    thresholdKey: k.critCrpThr,
  },
] as const

export type MafldCriterionDef = (typeof CRITERION_DEFS)[number]

export type MafldCriterion = MafldCriterionDef["id"]

export const CRITERIA_ORDER: readonly MafldCriterionDef[] = CRITERION_DEFS

export const MAFLD_CRITERIA: Record<MafldCriterion, MafldCriterionDef> =
  Object.fromEntries(CRITERION_DEFS.map((c) => [c.id, c])) as Record<
    MafldCriterion,
    MafldCriterionDef
  >

/**
 * The four the reader ticks, filtered from the one list above rather than
 * written out again, so a criterion cannot be a checkbox here and a derived
 * value there. The widget renders exactly these.
 */
export const DECLARED_CRITERIA = CRITERION_DEFS.filter(
  (c) => c.source === "declared"
)

export type DeclaredCriterion = (typeof DECLARED_CRITERIA)[number]["id"]

/** What the four checkboxes hold. Every key present, every value a real answer. */
export type DeclaredAnswers = Record<DeclaredCriterion, boolean>

/** All four unticked, which is what the widget starts from. */
export function noDeclaredAnswers(): DeclaredAnswers {
  return Object.fromEntries(
    DECLARED_CRITERIA.map((c) => [c.id, false])
  ) as DeclaredAnswers
}

export type MafldLimb = MafldLimbDef["id"]

/**
 * The three metabolic routes, in the consensus's own order.
 *
 * More than one can hold at once: a reader with a BMI of 33 and type 2
 * diabetes satisfies (a) and (b) both, and `assessMafld` returns every route
 * that fires rather than picking one. Which is not tidiness. A diabetic reader
 * told they meet the definition "because of their weight" would reasonably
 * conclude that losing weight settles it, and it does not.
 *
 * Route (c) is the only one that can be excluded by another: it applies to a
 * normal BMI alone, so it is mutually exclusive with (a) by construction.
 */
const LIMB_DEFS = [
  {
    id: "overweight",
    labelKey: k.limbOverweight,
    requirementKey: k.limbOverweightThr,
    bodyKey: k.limbOverweightBody,
  },
  {
    id: "diabetes",
    labelKey: k.limbDiabetes,
    requirementKey: k.limbDiabetesThr,
    bodyKey: k.limbDiabetesBody,
  },
  {
    id: "lean",
    labelKey: k.limbLean,
    requirementKey: k.limbLeanThr,
    bodyKey: k.limbLeanBody,
  },
] as const

export type MafldLimbDef = (typeof LIMB_DEFS)[number]

export const LIMB_ORDER: readonly MafldLimbDef[] = LIMB_DEFS

export const MAFLD_LIMBS: Record<MafldLimb, MafldLimbDef> =
  Object.fromEntries(LIMB_DEFS.map((l) => [l.id, l])) as Record<
    MafldLimb,
    MafldLimbDef
  >

// --- Outcomes ---------------------------------------------------------------

export type MafldOutcome = MafldOutcomeDef["id"]

/**
 * The four things this page can conclude, and the whole reason it is not a
 * two-state yes/no.
 *
 * `met` and `noSteatosis` are the easy pair. The other two are the ones a
 * two-state verdict would flatten and must not:
 *
 *   - `noMetabolic` is fat in the liver with no metabolic dysfunction at all.
 *     That is NOT MAFLD, and it is not good news either: it points the
 *     question at alcohol, a medication, or a rarer liver disease, which is a
 *     different conversation rather than the end of one. Folding it into a
 *     plain "no" would tell a reader with a fatty liver that nothing is wrong.
 *   - `indeterminate` is the metabolic half met and the steatosis half
 *     genuinely unresolved, which is where the Fatty Liver Index lands roughly
 *     a fifth of the people who use it. The honest output there is that one
 *     scan would settle it, and inventing a verdict instead is the single
 *     worst thing this page could do.
 *
 * `severity` orders them for the widget's colour choice only, and is not part
 * of the definition.
 */
const OUTCOME_DEFS = [
  {
    id: "met",
    severity: "high",
    labelKey: k.outcomeMet,
    meaningKey: k.outcomeMetMeaning,
    nextKey: k.outcomeMetNext,
  },
  {
    id: "indeterminate",
    severity: "mid",
    labelKey: k.outcomeIndeterminate,
    meaningKey: k.outcomeIndeterminateMeaning,
    nextKey: k.outcomeIndeterminateNext,
  },
  {
    id: "noMetabolic",
    severity: "mid",
    labelKey: k.outcomeNoMetabolic,
    meaningKey: k.outcomeNoMetabolicMeaning,
    nextKey: k.outcomeNoMetabolicNext,
  },
  {
    id: "noSteatosis",
    severity: "low",
    labelKey: k.outcomeNoSteatosis,
    meaningKey: k.outcomeNoSteatosisMeaning,
    nextKey: k.outcomeNoSteatosisNext,
  },
] as const

export type MafldOutcomeDef = (typeof OUTCOME_DEFS)[number]

export const OUTCOME_ORDER: readonly MafldOutcomeDef[] = OUTCOME_DEFS

export const MAFLD_OUTCOMES: Record<MafldOutcome, MafldOutcomeDef> =
  Object.fromEntries(OUTCOME_DEFS.map((o) => [o.id, o])) as Record<
    MafldOutcome,
    MafldOutcomeDef
  >

/**
 * Where the steatosis half stands, once the reader's own answer and the index
 * have been reconciled.
 *
 * Five states rather than three because a scan and an estimate are not the
 * same evidence, and the result panel says which one it used. `confirmed` and
 * `excluded` come from imaging the reader declares; the other three are the
 * index's three bands.
 */
export type SteatosisState =
  | "confirmed"
  | "excluded"
  | "likely"
  | "indeterminate"
  | "unlikely"

/** Whether `state` is evidence enough for the definition's first half. */
export function steatosisEstablished(state: SteatosisState): boolean {
  return state === "confirmed" || state === "likely"
}

/** Whether `state` argues against it. */
export function steatosisRuledOut(state: SteatosisState): boolean {
  return state === "excluded" || state === "unlikely"
}

// --- Input, refusal, assessment ---------------------------------------------

/** What the form collects, before the BMI and the index are derived from it. */
export interface MafldMeasurements {
  sex: MafldSex
  heightCm: number
  weightKg: number
  /** Waist circumference, cm, measured at the navel. */
  waistCm: number
  /** Triglycerides, mmol/L. */
  triglycerides: number
  /** GGT, U/L. */
  ggt: number
  /** Type 2 diabetes, diagnosed. */
  diabetes: boolean
  /** What a scan has or has not shown. */
  steatosis: SteatosisAnswer
  declared: DeclaredAnswers
}

/** Why an otherwise complete set of inputs cannot be assessed. */
export type MafldProblem =
  | "triglyceridesUnit"
  | "bodyOutOfRange"
  | "outOfRange"

/**
 * The first reason `m` cannot be assessed, or null when it can.
 *
 * The order is the order of the messages' usefulness, not the order of the
 * fields, and each message names the boxes the reader can actually act on:
 *
 *   - A triglyceride value above the accepted maximum comes first and gets its
 *     own message. It is the single most likely mistake on this page: the same
 *     result is written 1.8 in Switzerland and 159 in the American
 *     convention, and "check your entries" would send a reader hunting for a
 *     typo that is not there. It is also the one that would produce a
 *     confidently wrong answer, since triglycerides raise the index: 159
 *     instead of 1.8 pushes almost anybody over 60 and prints "steatosis
 *     likely" on a clean liver.
 *   - Then height, weight and waist together with the BMI they imply, because
 *     a reader can only fix this by correcting one of those three boxes.
 *   - Then the plain plausibility bounds on the rest.
 */
export function mafldProblem(m: MafldMeasurements): MafldProblem | null {
  if (m.triglycerides > MAX_TRIGLYCERIDES) {
    return "triglyceridesUnit"
  }
  const bmi = bmiFrom(m.heightCm, m.weightKg)
  if (
    m.heightCm < MIN_HEIGHT_CM ||
    m.heightCm > MAX_HEIGHT_CM ||
    m.weightKg < MIN_WEIGHT_KG ||
    m.weightKg > MAX_WEIGHT_KG ||
    m.waistCm < MIN_WAIST_CM ||
    m.waistCm > MAX_WAIST_CM ||
    bmi < MIN_BMI ||
    bmi > MAX_BMI
  ) {
    return "bodyOutOfRange"
  }
  if (
    m.triglycerides < MIN_TRIGLYCERIDES ||
    m.ggt < MIN_GGT ||
    m.ggt > MAX_GGT
  ) {
    return "outOfRange"
  }
  return null
}

export interface MafldAssessment {
  /** The reader's BMI, one decimal, as shown beside the result. */
  bmi: number
  /** The Fatty Liver Index, one decimal. Computed even when unused. */
  fli: number
  fliBand: FliBand
  /** Where the steatosis half landed, and on what evidence. */
  steatosis: SteatosisState
  /** Every metabolic route that fires, in the consensus's order. Possibly empty. */
  limbs: readonly MafldLimb[]
  /** Every criterion of route (c) that is met, in the consensus's order. */
  criteriaMet: readonly MafldCriterion[]
  outcome: MafldOutcome
  /**
   * True when route (c) was the reader's only way to the metabolic half, it
   * did not fire, none of the four declared boxes was ticked, and the marker
   * count is therefore the one thing standing between this reader and a
   * different verdict. See `CRITERION_DEFS` for why that needs saying out
   * loud.
   *
   * The last clause is what keeps the note honest. A reader whose fat has been
   * ruled out cannot reach `met` by ticking anything, because without fat
   * there is no MAFLD at any level of metabolic dysfunction, so telling them
   * their result is "an underestimate" would imply an answer the boxes cannot
   * change.
   */
  leanUnanswered: boolean
}

/**
 * The verdict.
 *
 * The index is computed whatever the reader answered about imaging, and shown
 * either way: it is the number on this page, and a reader whose scan already
 * found fat is still entitled to see where the estimate would have put them.
 * What the declared answer changes is which evidence the VERDICT rests on.
 *
 * The order of the three outcome tests is the part to read twice, because two
 * of the three orderings are defensible and only one is right:
 *
 *   1. steatosis ruled out wins first. Without fat in the liver there is no
 *      MAFLD however much metabolic dysfunction there is, and telling that
 *      reader anything else would be the definition inverted.
 *   2. no metabolic route comes before an unresolved steatosis half. A reader
 *      with no dysfunction at all does not meet the definition even if a scan
 *      would find fat, so resolving the steatosis question for them would be
 *      a scan that cannot change the answer.
 *   3. only then does an unresolved index mean unresolved.
 */
export function assessMafld(m: MafldMeasurements): MafldAssessment {
  const rawBmi = bmiFrom(m.heightCm, m.weightKg)
  const fli = roundFli(
    fattyLiverIndex({
      bmi: rawBmi,
      waistCm: m.waistCm,
      triglycerides: m.triglycerides,
      ggt: m.ggt,
    })
  )
  const fliBand = fliBandFor(fli)

  const steatosis: SteatosisState =
    m.steatosis === "confirmed"
      ? "confirmed"
      : m.steatosis === "excluded"
        ? "excluded"
        : fliBand

  const criteriaMet = CRITERIA_ORDER.filter((criterion) => {
    switch (criterion.id) {
      case "waist":
        return m.waistCm >= WAIST_FROM[m.sex]
      case "triglycerides":
        return m.triglycerides >= TRIGLYCERIDES_FROM
      default:
        return m.declared[criterion.id as DeclaredCriterion]
    }
  }).map((criterion) => criterion.id)

  const overweight = rawBmi >= OVERWEIGHT_BMI_FROM
  const lean =
    !overweight && !m.diabetes && criteriaMet.length >= LEAN_CRITERIA_NEEDED

  const limbs = LIMB_ORDER.filter((limb) =>
    limb.id === "overweight"
      ? overweight
      : limb.id === "diabetes"
        ? m.diabetes
        : lean
  ).map((limb) => limb.id)

  const outcome: MafldOutcome = steatosisRuledOut(steatosis)
    ? "noSteatosis"
    : limbs.length === 0
      ? "noMetabolic"
      : steatosisEstablished(steatosis)
        ? "met"
        : "indeterminate"

  return {
    bmi: roundBmi(rawBmi),
    fli,
    fliBand,
    steatosis,
    limbs,
    criteriaMet,
    outcome,
    leanUnanswered:
      !overweight &&
      !m.diabetes &&
      !lean &&
      !steatosisRuledOut(steatosis) &&
      DECLARED_CRITERIA.every(({ id }) => !m.declared[id]),
  }
}
